/**
 * metaLeadGen/calculations.ts
 *
 * Core metric calculations.
 *
 * Rules:
 * - Always aggregate raw totals FIRST, then calculate ratios.
 * - Never average CPM, CTR, bounceRate, conversionRate, or CPL from rows.
 * - All division is safe — returns 0 instead of NaN or Infinity.
 */

import type { MetaLeadGenRow, MetaLeadGenTotals, MetaLeadGenMetrics, PeriodComparison } from "./types";

// ─────────────────────────────────────────────
// SAFE DIVISION — never returns NaN or Infinity
// ─────────────────────────────────────────────
export function safeDivide(numerator: number, denominator: number): number {
  if (denominator === 0 || !isFinite(denominator) || !isFinite(numerator)) return 0;
  const result = numerator / denominator;
  return isFinite(result) ? result : 0;
}

// ─────────────────────────────────────────────
// AGGREGATION — sum raw totals from rows
// Returns clean zero-state if rows is empty.
// ─────────────────────────────────────────────
export function aggregateTotals(rows: MetaLeadGenRow[]): MetaLeadGenTotals {
  if (rows.length === 0) {
    return {
      spend: 0, impressions: 0, clicks: 0, landingPageViews: 0,
      leads: 0, bounces: 0, reach: 0, purchases: 0,
    };
  }

  return rows.reduce(
    (acc, row) => ({
      spend: acc.spend + row.spend,
      impressions: acc.impressions + row.impressions,
      clicks: acc.clicks + row.clicks,
      landingPageViews: acc.landingPageViews + row.landingPageViews,
      leads: acc.leads + row.leads,
      bounces: acc.bounces + row.bounces,
      reach: acc.reach + (row.reach ?? 0),
      purchases: acc.purchases + (row.qualifiedLeads ?? 0),
    }),
    { spend: 0, impressions: 0, clicks: 0, landingPageViews: 0, leads: 0, bounces: 0, reach: 0, purchases: 0 }
  );
}

// ─────────────────────────────────────────────
// DERIVED METRICS — calculated from aggregated totals only
// ─────────────────────────────────────────────
export function calculateMetrics(totals: MetaLeadGenTotals): MetaLeadGenMetrics {
  return {
    ...totals,
    cpm: safeDivide(totals.spend, totals.impressions) * 1000,
    ctr: safeDivide(totals.clicks, totals.impressions) * 100,
    conversionRate: safeDivide(totals.leads, totals.landingPageViews) * 100,
    bounceRate: safeDivide(totals.bounces, totals.landingPageViews) * 100,
    cpl: safeDivide(totals.spend, totals.leads),
    frequency: safeDivide(totals.impressions, totals.reach),
    costPerPurchase: safeDivide(totals.spend, totals.purchases),
  };
}

// ─────────────────────────────────────────────
// PERCENT CHANGE — for KPI arrows
// Returns 0 if previous is 0 (avoids divide-by-zero).
// ─────────────────────────────────────────────
export function percentChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return safeDivide(current - previous, previous) * 100;
}

// ─────────────────────────────────────────────
// PERIOD COMPARISON — compares two sets of raw rows
// Handles empty arrays cleanly (returns all-zero metrics).
// ─────────────────────────────────────────────
export function comparePeriods(
  currentRows: MetaLeadGenRow[],
  previousRows: MetaLeadGenRow[]
): PeriodComparison {
  const current = calculateMetrics(aggregateTotals(currentRows));
  const previous = calculateMetrics(aggregateTotals(previousRows));

  const changes: Record<keyof MetaLeadGenMetrics, number> = {
    spend: percentChange(current.spend, previous.spend),
    impressions: percentChange(current.impressions, previous.impressions),
    clicks: percentChange(current.clicks, previous.clicks),
    landingPageViews: percentChange(current.landingPageViews, previous.landingPageViews),
    leads: percentChange(current.leads, previous.leads),
    bounces: percentChange(current.bounces, previous.bounces),
    reach: percentChange(current.reach, previous.reach),
    purchases: percentChange(current.purchases, previous.purchases),
    cpm: percentChange(current.cpm, previous.cpm),
    ctr: percentChange(current.ctr, previous.ctr),
    conversionRate: percentChange(current.conversionRate, previous.conversionRate),
    bounceRate: percentChange(current.bounceRate, previous.bounceRate),
    cpl: percentChange(current.cpl, previous.cpl),
    frequency: percentChange(current.frequency, previous.frequency),
    costPerPurchase: percentChange(current.costPerPurchase, previous.costPerPurchase),
  };

  return { current, previous, changes };
}
